/**
 * Génération locale des déclinaisons du nom du patient
 * Aucun envoi à une IA : tout est calculé côté client à partir des noms détectés.
 * Utilisé pour anonymiser AVANT toute requête vers une IA (conformité RGPD / anonymat).
 */

import type { DetectedName } from "@/lib/text-extractor";

export interface DeclensionEntry {
  format: string;
  value: string;
}

/**
 * Construit les déclinaisons (NOM, Prénom, NOM Prénom, etc.) à partir des noms détectés.
 * Même logique que l'API identify-patient mais 100 % locale, sans appel réseau.
 */
export function buildDeclensionsFromDetectedNames(
  detectedNames: DetectedName[]
): DeclensionEntry[] {
  if (!detectedNames.length) return [];

  const best = detectedNames.find((n) => n.confidence === "high") ?? detectedNames[0];
  const lastName = (best.lastName ?? best.fullName?.split(/\s+/).pop() ?? "").trim();
  const firstName = (best.firstName ?? best.fullName?.split(/\s+/)[0] ?? "").trim();
  const nom = lastName.toUpperCase();
  const prenom = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    : "";
  const nomPrenom = [nom, prenom].filter(Boolean).join(" ");
  const prenomNom = [prenom, nom].filter(Boolean).join(" ");

  const entries: DeclensionEntry[] = [
    { format: "NOM", value: nom },
    { format: "Prénom", value: prenom },
    { format: "NOM Prénom", value: nomPrenom },
    { format: "Prénom NOM", value: prenomNom },
    { format: "nom prénom", value: nomPrenom.toLowerCase() },
    { format: "prénom nom", value: prenomNom.toLowerCase() },
  ];

  if (nom && prenom) {
    const initiale = prenom.charAt(0).toUpperCase();
    entries.push(
      { format: "NOM P.", value: `${nom} ${initiale}.` },
      { format: "P. NOM", value: `${initiale}. ${nom}` },
      { format: "Nom P.", value: `${nom.charAt(0) + nom.slice(1).toLowerCase()} ${initiale}.` },
      { format: "P. Nom", value: `${initiale}. ${nom.charAt(0) + nom.slice(1).toLowerCase()}` }
    );
  }

  return entries.filter((e) => e.value.length > 0);
}

/** Normalise une chaîne pour comparaison (minuscules, sans accents, espaces collapsés) */
export function normalizeForMatch(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

export interface PatientForMatch {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string | null;
  nameDeclensions: string[] | null;
}

export interface MatchResult {
  matchedPatientId: string | null;
  confidence: "high" | "medium" | "low";
  reason: string;
}

/**
 * Match côté client : compare les déclinaisons du document aux patients existants.
 * Aucune donnée envoyée à une IA. Utilise noms (déclinaisons) et optionnellement date de naissance.
 */
export function matchDocumentToPatient(
  documentDeclensionValues: string[],
  documentBirthDate: string | null,
  patients: PatientForMatch[]
): MatchResult {
  if (!patients.length) {
    return { matchedPatientId: null, confidence: "low", reason: "Aucun patient en base" };
  }

  const docValues = documentDeclensionValues
    .map(normalizeForMatch)
    .filter(Boolean);
  if (!docValues.length) {
    return { matchedPatientId: null, confidence: "low", reason: "Aucun nom extrait du document" };
  }

  const docSet = new Set(docValues);
  let bestMatch: { patient: PatientForMatch; score: number } | null = null;

  for (const patient of patients) {
    const declensions = patient.nameDeclensions
      ? (Array.isArray(patient.nameDeclensions)
          ? patient.nameDeclensions
          : [])
      : [];
    const patientNames = [
      patient.lastName,
      patient.firstName,
      `${patient.lastName} ${patient.firstName}`,
      `${patient.firstName} ${patient.lastName}`,
      ...declensions,
    ]
      .map(normalizeForMatch)
      .filter(Boolean);
    const patientSet = new Set(patientNames);

    let score = 0;
    for (const docVal of docSet) {
      if (patientSet.has(docVal)) score += 2;
      else if ([...patientSet].some((p) => p.includes(docVal) || docVal.includes(p))) score += 1;
    }
    if (documentBirthDate && patient.birthDate) {
      const docNorm = documentBirthDate.replace(/-/g, "");
      const patNorm = patient.birthDate.replace(/-/g, "");
      if (docNorm === patNorm) score += 3;
    }
    if (score > 0 && (!bestMatch || score > bestMatch.score)) {
      bestMatch = { patient, score };
    }
  }

  if (!bestMatch) {
    return { matchedPatientId: null, confidence: "low", reason: "Aucune correspondance trouvée" };
  }

  const { patient, score } = bestMatch;
  const confidence: "high" | "medium" | "low" =
    score >= 5 ? "high" : score >= 3 ? "medium" : "low";
  const reason =
    confidence === "high"
      ? "Correspondance nom(s) et date de naissance"
      : confidence === "medium"
        ? "Correspondance partielle des déclinaisons"
        : "Correspondance faible";

  return {
    matchedPatientId: patient.id,
    confidence,
    reason,
  };
}

/**
 * Mots qui ne doivent jamais être considérés comme noms de praticiens.
 * Évite de remplacer par [ORTHOPTISTE] des termes courants ou médicaux
 * (forme, figure, lecture, perception, etc.) mal détectés comme "noms".
 */
const NOT_PRACTITIONER_WORDS = new Set([
  // Courants
  "la", "le", "les", "un", "une", "des", "du", "de", "et", "ou", "mais", "donc", "car", "que", "qui", "quoi", "dont", "où", "ce", "cette", "ces",
  "pas", "bon", "tout", "toute", "tous", "toutes", "autre", "autres", "même", "bien", "plus", "moins", "très", "peu", "chez", "sans", "avec", "pour", "par", "sur", "sous", "dans", "entre", "vers", "depuis", "pendant", "avant", "après", "contre", "malgré", "selon",
  // Médical / orthoptie
  "forme", "formes", "figure", "figures", "fond", "lecture", "ligne", "lignes", "perception", "capacité", "capacités", "sensorielles", "sensorielle", "visuelle", "visuelles", "motricité", "motrice", "réflexe", "réflexes", "constance", "convergence", "convergence", "examen", "examens", "vision", "acuité", "motilité", "poursuite", "poursuites", "saccades", "saccade", "discrimination", "mémoire", "relations", "spatiale", "spatiales", "séquentielle", "syncrétique", "closure", "analyse", "analysées", "normale", "normales", "patient", "patients", "âge", "activités", "activité", "vie", "courante", "objets", "objet", "détails", "détail", "écriture", "lecture", "mots", "mot", "écrit", "lecteurs", "lecteur", "confiants", "rapides", "instantané", "contexte", "mul", "révèlent", "révéler", "peut", "pouvant", "expliquer", "difficultés", "difficulté", "morphologiques", "orientés", "entres", "entre", "elle", "elles", "modification", "conservation", "dimension", "ordonnancement", "position", "longueurs", "tailles", "orientations", "jugement", "milieu", "sélection", "résultat", "résultats", "absence", "présence", "probable", "fortement", "déficit", "stratégie", "stratégies", "exploration", "linéaire", "retour", "oubli", "test", "tests", "type", "organisation", "geste", "posture", "mouvements", "coordination", "localisation", "piquage", "thomas", "étoile", "barrage", "superposition", "coccimod", "pvse", "dem",
  // Titres / lieux
  "bilan", "orthoptique", "orthoptiste", "consultation", "centre", "médical", "cabinet", "docteur", "dr", "mlle", "prof", "maître", "dernier", "rééducation", "rééducations", "séances", "séance", "conclusion", "motif", "initialement", "cf", "cr", "contrôle", "prochaine", "cs", "exo", "colonnes", "chiffre", "chiffres", "mois", "point", "refaire", "hw", "quasi", "symétrique", "hess", "weiss", "coordimètre", "encadrées", "performante", "mètre", "mètres", "stéréoscopique", "tno", "arc", "oculo", "motrices", "limitations", "vues", "diplopie", "perçue", "rdc", "ppc", "ese", "près", "loin", "calibrées", "endurantes", "quasi-normal", "discrimination", "inhiber", "distracteurs", "représentée", "partiellement", "rotation", "mentale", "tendance", "transformer", "orthographier", "correctement", "confiants", "rapides", "instantané", "influencé", "ponctuel",
  // Mois, jours
  "janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche",
]);

/** Retourne true si le mot ne doit pas être utilisé comme nom de praticien. */
function isNotPractitionerWord(word: string): boolean {
  if (!word || word.length < 2) return true;
  const n = normalizeForMatch(word);
  return NOT_PRACTITIONER_WORDS.has(n);
}

/** Retourne true si la chaîne ressemble à un nom de personne (praticien), pas à un terme médical ou courant. */
function looksLikePersonName(fullName: string, firstName?: string, lastName?: string): boolean {
  const full = fullName.trim();
  if (full.length < 4) return false;
  const parts = full.split(/\s+/).map((p) => p.trim()).filter(Boolean);
  if (parts.some((p) => isNotPractitionerWord(p))) return false;
  if (parts.length === 1) return false;
  if (parts.some((p) => p.length < 2)) return false;
  return true;
}

/**
 * À partir des noms détectés et des déclinaisons du patient (retour IA),
 * retourne la liste des noms qui ne sont PAS le patient (orthoptiste, etc.)
 * pour les remplacer par [ORTHOPTISTE] lors de l'anonymisation.
 * Ne garde que les chaînes qui ressemblent à de vrais noms de personnes.
 */
export function getNonPatientNamesForAnonymization(
  detectedNames: { fullName: string; firstName?: string; lastName?: string }[],
  patientDeclensionValues: string[]
): string[] {
  const patientSet = new Set(
    patientDeclensionValues.map(normalizeForMatch).filter(Boolean)
  );
  const other: string[] = [];
  for (const n of detectedNames) {
    const full = (n.fullName ?? "").trim();
    const nomPrenom = [n.lastName, n.firstName].filter(Boolean).join(" ").trim();
    const prenomNom = [n.firstName, n.lastName].filter(Boolean).join(" ").trim();
    const normalized = [
      normalizeForMatch(full),
      normalizeForMatch(nomPrenom),
      normalizeForMatch(prenomNom),
    ].filter(Boolean);
    const isPatient = normalized.some((v) => patientSet.has(v));
    if (!isPatient && full.length >= 2 && looksLikePersonName(full, n.firstName, n.lastName)) {
      other.push(full);
      if (n.lastName && n.lastName !== full && !isNotPractitionerWord(n.lastName) && n.lastName.length >= 3) other.push(n.lastName);
      if (n.firstName && n.firstName !== full && !isNotPractitionerWord(n.firstName) && n.firstName.length >= 3) other.push(n.firstName);
    }
  }
  return [...new Set(other)].filter(Boolean);
}
